import { useContext, useEffect, useRef } from 'react';
import router from 'next/router';

import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ModalContext } from 'src/context/Modal/ModalContext';

import useContentAccessLevel from 'src/hooks/useContentAccessLevel';
import { useLoginReturnUrl } from 'src/hooks/useLoginReturnUrls';

import { SitecoreContextValue } from 'src/lib/ComponentProps';

import { ContentDetailItem } from 'src/types/ContentDetailsItem';

import { LoginFormFields } from 'src/components/account/Login/LoginFormFields';
import { Button } from 'src/components/basicContent/_Button/Button';

import ContentDetailFields from '../ContentDetailFields';

import { ContentDetailsHeader } from './_ContentDetailTypesComponents/ContentDetailsHeader';
import { ContentDetailsMain } from './_ContentDetailTypesComponents/ContentDetailsMain';

import ctaStyles from '../CourseDetailCallToAction.module.css';
import overviewStyles from '../CourseDetailOverview.module.css';

type clinicalTopicProps = {
	ContentDetail: ContentDetailItem;
	fields: ContentDetailFields & LoginFormFields;
};

export function ClinicalTopic(props: clinicalTopicProps): JSX.Element {
	// JR Engineer note - this component can be deleted once all clicncal topics have moved to the new
	// resource template type
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const { isAllowedAccessToContent } = useContentAccessLevel(props.ContentDetail.Access);
	const ModalState = useContext(ModalContext);

	const whyCantParagraph = useRef<HTMLDivElement>(null);

	const { getLoginReturnDestinationForElement } = useLoginReturnUrl(whyCantParagraph);

	useEffect(() => {
		getLoginReturnDestinationForElement();
	}, []);

	return (
		<section className="maxWidth">
			<header className={`${ctaStyles.row} padX`} id="header">
				<ContentDetailsHeader
					AccessType={props.fields.AccessType}
					LearningType={props.ContentDetail.LearningTypeString}
					AccessTypesString={props.ContentDetail.AccessTypesString}
					TypesOfResourcesString={props.ContentDetail.TypesOfResourcesString}
					OpenAccessText={props.fields.OpenAccessText}
					RegisteredUserText={props.fields.RegisteredUserText}
					RCNMemberText={props.fields.RCNMemberText}
					RCNiPlusSubscriberText={props.fields.RCNiPlusSubscriberText}
					OrganisationalProductText={props.fields.OrganisationalProductText}
					Cost={props.ContentDetail.Cost}
					Location={props.ContentDetail.Location}
				/>

				<div className={overviewStyles.buttonWrapper}>
					{isAllowedAccessToContent ? (
						<Button
							type="button"
							title={sitecoreContext.dictionary.viewResourceButtonText}
							className="orange button"
							onClick={() => router.push({ pathname: props.fields?.WildCardContentUrl })}
						>
							<Text field={{ value: sitecoreContext.dictionary.viewResourceButtonText }} />
						</Button>
					) : (
						<Button
							type="button"
							title={sitecoreContext.dictionary.viewResourceButtonText}
							className="orange button"
							onClick={() =>
								ModalState.updateModalInfo({
									LoginFormModal: {
										Display: true,
										Data: undefined,
									},
									CourseEnrolModal: undefined,
									CourseRegisterInterestModal: undefined,
								})
							}
						>
							<Text field={{ value: sitecoreContext.dictionary.viewResourceButtonText }} />
						</Button>
					)}
				</div>
			</header>

			<main className={`${overviewStyles.section} padX`} id={props.fields.Title?.value.toLowerCase().split(' ').join('_')}>
				<ContentDetailsMain
					Information={props.ContentDetail.Information}
					CertificateOfCompletion={props.ContentDetail.CertificateOfCompletion}
					DateUpdated={props.ContentDetail.DateUpdated}
					DateUpdatedText={props.fields.DateUpdatedText}
					DateUpdatedTextPlaceholder={props.fields.DateUpdatedTextPlaceholder}
					ProfessionalLeads={props.ContentDetail.ProfessionalLeads}
					ProfessionalLeadsText={props.fields.ProfessionalLeadsText}
					ProfessionalLeadsTextPlaceholder={props.fields.ProfessionalLeadsTextPlaceholder}
					ResourceSponsor={props.ContentDetail.ResourceSponsor}
					HasAccess={props.ContentDetail.Access?.RcniPlusSubscriber.AllowAccess}
					QualityAssuranceText={props.fields.QualityAssuranceText}
				/>
			</main>
		</section>
	);
}
